import { useInitiateSealdIdentity } from "apollo/hooks/mutations";
import { useEnvContext } from "context/EnvContext";
import api from "core/model/api";
import { getErrorMessage } from "core/model/utils/errors";
import {
  computeSealdDisplayId,
  createIdentityForAccount,
  getSealdSDKInstance,
  safeAddGroupMembers,
} from "core/seald";
import { Account } from "core/types";
import { useTracking } from "react-tracking";
import { useLocale } from "translations";

export function useAddAccountToSealdGroup() {
  const { trackEvent } = useTracking();
  const envContext = useEnvContext();
  const [initiateSealdIdentity] = useInitiateSealdIdentity();
  const localeString = useLocale();

  async function addAccountToSealdGroup({
    account,
    sealdId,
  }: {
    account: Account;
    sealdId: string | undefined;
  }) {
    try {
      if (!sealdId || !account) {
        throw new Error("Account and group ID are required");
      }

      // Create an account and add it to Seald group
      const accountToAdd = await createIdentityForAccount({
        account,
        envContext,
        initiateSealdIdentity,
        localeString,
        trackEvent,
      });

      if (!accountToAdd) {
        throw new Error("Must have an account to add");
      }

      const accountToAddUserId = computeSealdDisplayId({
        envContext,
        id: accountToAdd.id,
        type: "account",
      });

      const sealdSDKInstance = await getSealdSDKInstance();

      // Add the new account to the Seald group and set as admin
      await safeAddGroupMembers({
        groupId: sealdId,
        newAdmins: { userIds: [accountToAddUserId] },
        newMembers: { userIds: [accountToAddUserId] },
        sealdSDKInstance,
      });

      // Create Seald access
      await api.crypto.createSealdAccess({
        account_id: accountToAdd.id,
        seald_id: sealdId,
      });
    } catch (err) {
      console.error(`[addAccountToSealdGroup] ${getErrorMessage(err)}`);
    }
  }

  return [addAccountToSealdGroup] as const;
}
